// Időkapu-foglaló · iter147 — Napló / Előzmény (booking audit).
//
// <BookingAudit bookingId={id} authHeaderFn?={fn} defaultOpen={false} />
//
// Fetches the audit trail via GET /api/dock-scheduling/bookings/{id}/audit
// and renders a compact list (newest first). Collapsible; suitable for
// embedding inside the Slot-jegy modal AND the gate-lookup detail panel.

import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, ChevronDown, ChevronRight, User, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

const EVENT_LABEL_HU = {
  created: "Létrehozva",
  field_modified: "Módosítva",
  arrival_marked: "Érkezés jelölés",
  cancelled: "Lemondva",
  series_created: "Sorozat létrehozva",
};

const EVENT_COLOR = {
  created: "#22c55e",
  field_modified: "#f3b14a",
  arrival_marked: BLUE,
  cancelled: "#f87171",
  series_created: BLUE,
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

function EventPayload({ e }) {
  const payload = e.payload || {};
  if (e.event_type === "field_modified") {
    const changes = payload.changes || {};
    const parts = [];
    for (const [k, v] of Object.entries(changes)) {
      if (k === "field_values") {
        for (const [fk, fv] of Object.entries(v || {})) {
          parts.push(`${fk}: ${JSON.stringify(fv.old)} → ${JSON.stringify(fv.new)}`);
        }
      } else {
        parts.push(`${k}: ${JSON.stringify(v.old)} → ${JSON.stringify(v.new)}`);
      }
    }
    if (parts.length === 0) return null;
    return (
      <div style={mono(10, "var(--dimmer)")}>
        {parts.map((p, i) => <div key={i}>{p}</div>)}
      </div>
    );
  }
  if (e.event_type === "arrival_marked") {
    return (
      <div style={mono(10, "var(--dimmer)")}>
        {payload.old_status || "—"} → <b>{payload.new_status}</b>
        {payload.grace_minutes !== undefined && <span> · grace {payload.grace_minutes} perc</span>}
      </div>
    );
  }
  if (e.event_type === "cancelled") {
    return (
      <div style={mono(10, "var(--dimmer)")}>
        scope: {payload.scope || "this"}
      </div>
    );
  }
  if (e.event_type === "series_created") {
    return (
      <div style={mono(10, "var(--dimmer)")}>
        {payload.created_count} létrehozva
        {payload.skipped_count > 0 && `, ${payload.skipped_count} kihagyva`}
      </div>
    );
  }
  if (e.event_type === "created") {
    return (
      <div style={mono(10, "var(--dimmer)")}>
        ref: {payload.reference || "—"}
      </div>
    );
  }
  return null;
}

export default function BookingAudit({ bookingId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ds-audit", bookingId],
    queryFn: () => axios.get(`${API}/bookings/${bookingId}/audit`).then(r => r.data),
    enabled: !!bookingId && open,
    retry: false,
  });
  const items = data?.items || [];

  return (
    <div data-testid="ds-booking-audit" className="mt-3 rounded"
      style={{ border: "1px solid rgba(148,163,184,0.20)", background: "var(--bg-surface, #0d0f18)" }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="ds-booking-audit-toggle"
        className="w-full flex items-center gap-2 px-3 py-2"
        style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--text)" }}
      >
        {open ? <ChevronDown size={14} color={BLUE} /> : <ChevronRight size={14} color={BLUE} />}
        <ClipboardList size={14} color={BLUE} />
        <span style={syne(12, 700)}>Napló / Előzmény</span>
        {open && items.length > 0 && (
          <span className="ml-2 px-1.5 py-0.5 rounded" style={{ background: "rgba(24,95,165,0.15)", ...mono(9, BLUE, 700, "0.4px") }}>
            {items.length}
          </span>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3">
          {isLoading && <div style={mono(11, "var(--dimmer)")}>Betöltés…</div>}
          {isError && <div style={mono(11, "#f87171")}>Nem sikerült betölteni a naplót.</div>}
          {!isLoading && items.length === 0 && <div style={mono(11, "var(--dimmer)")}>Még nincs napló bejegyzés.</div>}
          <div>
            {items.map((e, i) => (
              <div
                key={e.id || i}
                data-testid={`ds-audit-row-${i}`}
                className="flex items-start gap-2 py-1.5"
                style={{ borderTop: i > 0 ? "1px dashed rgba(148,163,184,0.20)" : "none" }}
              >
                <span
                  style={{
                    width: 8, height: 8, borderRadius: 999,
                    background: EVENT_COLOR[e.event_type] || "var(--dimmer)",
                    marginTop: 6, flexShrink: 0,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div style={mono(11, "var(--text)", 700, "0.4px")}>
                    {EVENT_LABEL_HU[e.event_type] || e.event_type}
                  </div>
                  <EventPayload e={e} />
                  <div className="mt-0.5 flex flex-wrap items-center gap-2" style={mono(9, "var(--dimmer)", 700, "0.3px")}>
                    <span><Clock size={9} style={{ display: "inline", verticalAlign: -1 }} /> {fmtTs(e.at)}</span>
                    <span><User size={9} style={{ display: "inline", verticalAlign: -1 }} /> {e.actor_email || e.actor_user_id || "—"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
